

import {
    CheckCircle as CheckCircle2,
    DragIndicator as GripVertical,
    Info,
    Add as Plus,
    Search
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Event } from '@/lib/types';

interface ArcEventSidebarProps {
  events: Event[];
  existingEventIds: string[];
  onCreateNew: () => void;
  onViewDetails: (eventId: string) => void;
}

export function ArcEventSidebar({
  events,
  existingEventIds,
  onCreateNew,
  onViewDetails,
}: ArcEventSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [hideExisting, setHideExisting] = useState(false);
  const { t } = useTranslation();

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      const matchesSearch = e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase());

      const isAlreadyInArc = existingEventIds.includes(e.id);

      if (hideExisting && isAlreadyInArc) return false;

      return matchesSearch;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [events, searchQuery, hideExisting, existingEventIds]);

  const handleDragStart = (e: React.DragEvent, eventId: string) => {
    e.dataTransfer.setData('application/reactflow', eventId);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('arcs.event_library')}
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder={t('arcs.search_events')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={hideExisting}
              onChange={(e) => setHideExisting(e.target.checked)}
            />
          }
          label={<Typography variant="caption">{t('arcs.hide_events_in_arc')}</Typography>}
          sx={{ mb: 1, ml: 0 }}
        />

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Plus sx={{ fontSize: 18 }} />}
          onClick={onCreateNew}
          sx={{ mb: 2, borderStyle: 'dashed' }}
        >
          {t('arcs.create_new_event')}
        </Button>
      </Box>

      <Divider />

      <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filteredEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="body2">{t('arcs.no_events_found')}</Typography>
          </Box>
        ) : (
          filteredEvents.map((event) => {
            const isAlreadyInArc = existingEventIds.includes(event.id);

            return (
              <ListItem
                key={event.id}
                disablePadding
                sx={{ mb: 1 }}
              >
                <Paper
                  variant="outlined"
                  draggable={!isAlreadyInArc}
                  onDragStart={(e) => handleDragStart(e, event.id)}
                  sx={{
                    width: '100%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: isAlreadyInArc ? 'default' : 'grab',
                    opacity: isAlreadyInArc ? 0.6 : 1,
                    bgcolor: isAlreadyInArc ? 'action.hover' : 'background.paper',
                    '&:hover': {
                      bgcolor: isAlreadyInArc ? 'action.hover' : 'action.hover',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {!isAlreadyInArc && (
                    <Box sx={{ color: 'text.disabled', display: 'flex' }}>
                      <GripVertical sx={{ fontSize: 16 }} />
                    </Box>
                  )}

                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {event.name}
                      </Typography>
                    }
                  />

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {isAlreadyInArc ? (
                      <Tooltip title={t('arcs.already_in_arc')}>
                        <CheckCircle2 sx={{ fontSize: 16, color: '#10b981' }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('arcs.view_details')}>
                        <IconButton size="small" onClick={() => onViewDetails(event.id)}>
                          <Info sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              </ListItem>
            );
          })
        )}
      </List>
    </Box>
  );
}
